import { useCallback, useEffect, useMemo, useState } from "react";

import AttendanceFilters from "./components/AttendanceFilters";
import AttendanceTable from "./components/AttendanceTable";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';

function indiaToday() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

export default function DailyAttendance() {
  const { accessToken } = useAuth();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [hrmsFilters, setHrmsFilters] = useState({});
  const [selectedDate, setSelectedDate] = useState(indiaToday());
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAttendance = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest(
        `/attendance/admin/daily?date=${selectedDate}`,
        { accessToken },
      );

      setRecords(response?.employees || []);
    } catch (requestError) {
      setError(
        requestError.message || "Unable to load attendance records.",
      );
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedDate]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return records.filter((employee) => {
      const matchesSearch =
        !normalizedSearch ||
        employee.fullName?.toLowerCase().includes(normalizedSearch) ||
        employee.employeeCode?.toLowerCase().includes(normalizedSearch);

      const employeeStatus =
        {
          PRESENT: "Present",
          LATE: "Late",
          ABSENT: "Absent",
          ON_LEAVE: "On Leave",
          HALF_DAY: "Half Day",
          WEEKLY_OFF: "Weekly Off",
          HOLIDAY: "Holiday",
        }[employee.status] || employee.status;

      const matchesStatus =
        status === "All" || employeeStatus === status;

      return matchesHrmsFilters(employee,hrmsFilters) && matchesSearch && matchesStatus;
    });
  }, [records, search, status,hrmsFilters]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Daily Attendance
        </h1>

        <p className="mt-2 text-gray-500">
          Track employee attendance for a selected day.
        </p>
      </div>

      <AttendanceFilters
        search={search}
        setSearch={setSearch}
        status={status}
        setStatus={setStatus}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <HrmsFilters records={records} value={hrmsFilters} onChange={setHrmsFilters} />
      <AttendanceTable
        records={filteredRecords}
        isLoading={isLoading}
      />
    </div>
  );
}
